/**
 * API helper for Lorena Cleaning - calls Netlify functions that use Firebase
 */

import { agentDebugLog } from "./utils/agentDebugLog";
import { assertPayloadUnderLimit } from "./utils/payloadSizeGuard";
import { encodeBookingFieldsForWire, type EncodeInput } from "./utils/bookingWireCodec";

/** Firebase Web app id from Vite (`VITE_FIREBASE_APP_ID`); stamped on booking writes when set. */
function viteFirebaseAppIdForWire(): string | undefined {
  const raw = import.meta.env.VITE_FIREBASE_APP_ID;
  const s = typeof raw === "string" ? raw.trim() : "";
  return s || undefined;
}

function withViteFirebaseAppIdForWire(obj: EncodeInput): EncodeInput {
  const appId = viteFirebaseAppIdForWire();
  return appId ? { ...obj, appId } : obj;
}

// When running via Netlify dev, functions are typically served from http://localhost:8888.
// In production, window.location.origin already includes the correct domain.
const API_BASE = typeof window !== "undefined"
  ? (window.location.hostname === "localhost" && window.location.port && window.location.port !== "8888"
      ? "http://localhost:8888/.netlify/functions"
      : `${window.location.origin}/.netlify/functions`)
  : "";

/** Server-signed session token storage (set after admin/team password login). Never use VITE_ for secrets. */
export const LORENA_API_ACCESS_TOKEN_KEY = "lorena_api_access_token";

/** Client-side quota guard error codes (thrown from `call()`). */
export const QUOTA_CIRCUIT_BREAKER = "QUOTA_CIRCUIT_BREAKER";
export const API_COOLDOWN = "API_COOLDOWN";
/** Session hard-lock: too many kill-switch trips; refresh the page to reset. */
export const API_SESSION_HARD_LOCK = "API_SESSION_HARD_LOCK";

const QUOTA_KILL_SWITCH_TRIPS_BEFORE_HARD_LOCK = 3;

export type QuotaBannerReason = "kill_switch" | "cooldown" | "loop" | "hard_lock";

export const API_COOLDOWN_STORAGE_KEY = "api_cooldown_until";

const GLOBAL_REQUEST_WINDOW_MS = 60_000;
const GLOBAL_REQUEST_SAFE_CEILING = 300;
const GLOBAL_KILL_SWITCH_MS = 60_000;
const DEFAULT_429_COOLDOWN_MS = 60_000;

const apiRequestTimestamps: number[] = [];
let globalKillSwitchUntil = 0;
let quotaKillSwitchTripCount = 0;
let sessionApiHardLocked = false;
let sessionNetlifyFetchCount = 0;

/** Exported for Quota diagnostics (dev): POSTs to Netlify since this page load. */
export function getSessionNetlifyFetchCount(): number {
  return sessionNetlifyFetchCount;
}

export function isSessionApiHardLocked(): boolean {
  return sessionApiHardLocked;
}

export const LORENA_API_FETCH_COUNT_EVENT = "lorena:api-fetch-count";

function dispatchSessionFetchCount() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(LORENA_API_FETCH_COUNT_EVENT, { detail: { count: sessionNetlifyFetchCount } }),
  );
}

function dispatchQuotaEvent(reason: QuotaBannerReason) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("lorena:api-quota", { detail: { reason } }));
}

function pruneRequestWindow(now: number) {
  const cutoff = now - GLOBAL_REQUEST_WINDOW_MS;
  while (apiRequestTimestamps.length > 0 && apiRequestTimestamps[0]! < cutoff) {
    apiRequestTimestamps.shift();
  }
}

/** For admin UI: remaining ms until client allows API calls again (0 = none). */
export function getClientCooldownUntilMs(): number {
  if (typeof window === "undefined") return 0;
  const raw = localStorage.getItem(API_COOLDOWN_STORAGE_KEY);
  if (!raw) return 0;
  const until = Number(raw);
  return Number.isFinite(until) ? until : 0;
}

export function getQuotaBlockStatus(): { killSwitchUntil: number; cooldownUntil: number } {
  return {
    killSwitchUntil: globalKillSwitchUntil,
    cooldownUntil: getClientCooldownUntilMs(),
  };
}

/**
 * Enforce sliding-window limit + kill-switch for authenticated-style traffic.
 * Public/bootstrap calls (`isPublicApiCall`) bypass so login + blocked-days probe still work.
 */
function assertGlobalRequestBudgetOrThrow(now: number) {
  if (now < globalKillSwitchUntil) {
    const err = new Error("API paused: quota protection active. Try again in about a minute.") as Error & {
      code?: string;
    };
    err.code = QUOTA_CIRCUIT_BREAKER;
    throw err;
  }

  const cd = getClientCooldownUntilMs();
  if (cd > 0 && now < cd) {
    dispatchQuotaEvent("cooldown");
    const err = new Error("API cooldown after rate limit. Please wait before retrying.") as Error & { code?: string };
    err.code = API_COOLDOWN;
    throw err;
  }

  pruneRequestWindow(now);
  if (apiRequestTimestamps.length >= GLOBAL_REQUEST_SAFE_CEILING) {
    globalKillSwitchUntil = now + GLOBAL_KILL_SWITCH_MS;
    quotaKillSwitchTripCount += 1;
    if (quotaKillSwitchTripCount >= QUOTA_KILL_SWITCH_TRIPS_BEFORE_HARD_LOCK) {
      sessionApiHardLocked = true;
      dispatchQuotaEvent("hard_lock");
    } else {
      dispatchQuotaEvent("kill_switch");
    }
    const err = new Error(
      `Too many API requests in ${GLOBAL_REQUEST_WINDOW_MS / 1000}s. All calls paused for ${GLOBAL_KILL_SWITCH_MS / 1000}s.`,
    ) as Error & { code?: string };
    err.code = QUOTA_CIRCUIT_BREAKER;
    throw err;
  }

  apiRequestTimestamps.push(now);
}

let resolveBearerToken: (() => Promise<string | null>) | null = null;

/**
 * Registers how to obtain Authorization: Bearer for Netlify functions.
 * Prefer Firebase `getIdToken()`; fall back to Lorena session token from sessionStorage.
 */
export function setApiBearerResolver(fn: () => Promise<string | null>) {
  resolveBearerToken = fn;
}

function isPublicApiCall(fn: string, body: Record<string, unknown>): boolean {
  if (fn === "auth") {
    const a = body.action;
    return a === "login" || a === "signup" || a === "verifyEmail";
  }
  if (fn === "blocked-days" && body.action === "get") return true;
  return false;
}

function ensureArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function ensureStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
}

/**
 * Enhanced API call function with retry capability and detailed error information
 * @param fn Function name to call
 * @param body Request body
 * @param options Optional parameters (retries, timeout)
 * @returns Promise with response data
 */
/** Idempotent cached reads: 2 retries, exponential backoff 400ms..3s (minor network blips). */
const READ_RETRY_COUNT = 2;
const READ_RETRY_BASE_MS = 400;
const READ_RETRY_CAP_MS = 3000;

/** Mutations (non-idempotent Netlify POST bodies): 3 retries, separate backoff window. */
const MUTATION_RETRY_COUNT = 3;
const MUTATION_RETRY_BASE_MS = 500;
const MUTATION_RETRY_CAP_MS = 8000;

/** True when this call maps to a read/list/get — no automatic mutation retries. */

/** Netlify function names that receive `X-Admin-Secret` when a secret is available (must match server `ADMIN_SECRET`). */
const ADMIN_SCOPE_NETLIFY_FUNCTIONS = new Set([
  "bookings",
  "users",
  "inquiries",
  "blocked-days",
]);

/**
 * Same-tab override when `VITE_ADMIN_SECRET` is not baked into the bundle (e.g. preview builds).
 * After setting, call `notifyRuntimeAdminSecretUpdated()` so the admin calendar refetches immediately.
 */
export const LORENA_RUNTIME_ADMIN_SECRET_KEY = "lorena_runtime_admin_secret";

export const LORENA_RUNTIME_ADMIN_SECRET_EVENT = "lorena:runtime-admin-secret";

/** Effective client admin shared secret: build-time `VITE_ADMIN_SECRET`, then `localStorage` override. */
export function getEffectiveAdminSecret(): string {
  const fromEnv = import.meta.env.VITE_ADMIN_SECRET;
  const envSecret = typeof fromEnv === "string" ? fromEnv.trim() : "";
  if (envSecret) return envSecret;
  if (typeof window === "undefined") return "";
  try {
    const fromStore = localStorage.getItem(LORENA_RUNTIME_ADMIN_SECRET_KEY);
    return typeof fromStore === "string" ? fromStore.trim() : "";
  } catch {
    return "";
  }
}

/**
 * Adds `X-Admin-Secret` for admin-scoped Netlify functions when a secret is available.
 * Uses {@link getEffectiveAdminSecret} (trimmed `import.meta.env.VITE_ADMIN_SECRET`, else `localStorage` `lorena_runtime_admin_secret`).
 * Value must match the server `ADMIN_SECRET` on Netlify.
 */
function attachAdminSecretHeaderIfConfigured(fn: string, headers: Record<string, string>) {
  const secret = getEffectiveAdminSecret();
  if (!secret || !ADMIN_SCOPE_NETLIFY_FUNCTIONS.has(fn)) return;
  headers["X-Admin-Secret"] = secret;
}

function isReadOnlyNetlifyCall(fn: string, body: Record<string, unknown>): boolean {
  const a = String(body.action ?? "");
  switch (fn) {
    case "bookings":
      return [
        "listByUser",
        "listMine",
        "listPastByUser",
        "getById",
        "listAdminWindow",
        "listAll",
        "listBookingRequests",
        "listActivity",
        "listArchived",
        "listAssignedToMe",
      ].includes(a);
    case "payments":
      return a === "listByUser" || a === "listAll";
    case "blocked-days":
      return a === "get";
    case "inquiries":
      return a === "list";
    case "users":
      return ["listAll", "listTeam", "listClients", "getByEmail"].includes(a);
    default:
      return false;
  }
}

function makeApiRequestId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID().slice(0, 8);
  }
  return `r${Date.now().toString(36).slice(-10)}`;
}

async function call<T>(
  fn: string, 
  body: Record<string, unknown>, 
  options: { 
    retries?: number; 
    retryDelay?: number; 
    timeout?: number;
    /** When true with default retries, uses READ_RETRY_COUNT and exponential backoff between attempts. */
    exponentialBackoff?: boolean;
    /** Merged into fetch headers (e.g. dev-only diagnostics). */
    extraHeaders?: Record<string, string>;
  } = {}
): Promise<T> {
  const requestId = makeApiRequestId();
  const publicCall = isPublicApiCall(fn, body);
  if (!publicCall && typeof window !== "undefined" && sessionApiHardLocked) {
    const err = new Error(
      "Quota protection locked this session after repeated overloads. Refresh the page to reset.",
    ) as Error & { code?: string };
    err.code = API_SESSION_HARD_LOCK;
    throw err;
  }
  const readOnly = isReadOnlyNetlifyCall(fn, body);
  const explicitBackoff = options.exponentialBackoff === true;
  const explicitRetries = options.retries;

  let retries: number;
  let exponentialBackoff: boolean;
  let useMutationBackoff: boolean;

  if (explicitRetries !== undefined) {
    retries = explicitRetries;
    exponentialBackoff = explicitBackoff;
    useMutationBackoff = false;
  } else if (explicitBackoff) {
    retries = READ_RETRY_COUNT;
    exponentialBackoff = true;
    useMutationBackoff = false;
  } else if (!publicCall && !readOnly) {
    retries = MUTATION_RETRY_COUNT;
    exponentialBackoff = true;
    useMutationBackoff = true;
  } else {
    retries = 0;
    exponentialBackoff = false;
    useMutationBackoff = false;
  }

  const retryDelay = options.retryDelay ?? 1000;
  const timeout = options.timeout ?? 30000;
  
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "X-Client-Request-Id": requestId,
    ...(options.extraHeaders && typeof options.extraHeaders === "object" ? options.extraHeaders : {}),
  };
  if (!isPublicApiCall(fn, body) && resolveBearerToken) {
    try {
      const token = await resolveBearerToken();
      if (token) headers["Authorization"] = `Bearer ${token}`;
    } catch {
      /* no token */
    }
  }

  attachAdminSecretHeaderIfConfigured(fn, headers);

  // One budget slot per logical `call()` — retries reuse the same slot (no burst inflation).
  if (!publicCall && typeof window !== "undefined") {
    assertGlobalRequestBudgetOrThrow(Date.now());
  }

  let lastError: Error | null = null;
  let attempt = 0;

  while (attempt <= retries) {
    try {
      // Create an AbortController for timeout handling
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      if (typeof window !== "undefined") {
        sessionNetlifyFetchCount += 1;
        dispatchSessionFetchCount();
      }

      if (import.meta.env.DEV) {
        const keys = Object.keys(headers);
        const hasXAdminSecret = keys.some((k) => k.toLowerCase() === "x-admin-secret");
        console.log(
          `[api] fetch → ${fn} header keys:`,
          keys.sort(),
          "| X-Admin-Secret present:",
          hasXAdminSecret,
        );
      }

      const res = await fetch(`${API_BASE}/${fn}`, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
        signal: controller.signal
      });
      
      // Clear the timeout since fetch completed
      clearTimeout(timeoutId);
      
      // Parse response data with fallback
      const data = await res.json().catch(() => ({}));
      
      // Handle non-OK responses
      if (!res.ok) {
        if (res.status === 429 && typeof window !== "undefined") {
          let waitMs = DEFAULT_429_COOLDOWN_MS;
          const ra = res.headers.get("Retry-After");
          if (ra) {
            const sec = Number(ra.trim());
            if (Number.isFinite(sec) && sec > 0) waitMs = Math.min(300_000, sec * 1000);
          }
          const end = Date.now() + waitMs;
          localStorage.setItem(API_COOLDOWN_STORAGE_KEY, String(end));
          dispatchQuotaEvent("cooldown");
        }
        if (res.status === 401 && typeof window !== "undefined") {
          invalidateApiCache();
          window.dispatchEvent(new CustomEvent("lorena:api-unauthorized"));
        }
        const errorMessage = (data as { error?: string; details?: string; code?: string }).error || 
                            `Request failed: ${res.status}`;
        const errorCode = (data as { code?: string }).code || `HTTP_${res.status}`;
        const errorDetails = (data as { details?: string }).details || '';
        
        const error = new Error(errorMessage) as Error & { 
          status?: number; 
          code?: string; 
          details?: string;
          isRetryable?: boolean;
        };
        
        error.status = res.status;
        error.code = errorCode;
        error.details = errorDetails;
        
        // Determine if error is retryable (5xx errors or specific codes)
        // Do not retry rate limits — avoids amplifying quota pressure.
        error.isRetryable = res.status !== 429 && (
                           res.status >= 500 ||
                           errorCode === 'ACCEPT_REQUEST_FAILED' ||
                           errorCode === 'DELETE_REQUEST_FAILED' ||
                           errorMessage.includes('RST_STREAM') ||
                           errorMessage.includes('INTERNAL'));
        
        // #region agent log
        if (fn === "bookings") {
          agentDebugLog({
            hypothesisId: "H1",
            location: "api.ts:call",
            message: "bookings_http_error",
            data: { action: String(body.action ?? ""), status: res.status, code: errorCode },
          });
        }
        // #endregion
        throw error;
      }
      
      // #region agent log
      if (fn === "bookings" && body.action === "deleteMany") {
        const d = data as { deletedCount?: number; requestedCount?: number; ok?: boolean };
        agentDebugLog({
          hypothesisId: "H2",
          location: "api.ts:call",
          message: "deleteMany_response_ok",
          data: {
            deletedCount: d.deletedCount,
            requestedCount: d.requestedCount,
            ok: d.ok,
          },
        });
      }
      // #endregion
      return data as T;
    } catch (err) {
      // Import error handler
      const { enhanceError, ErrorType } = await import('./utils/errorHandler');
      const error = err instanceof Error ? err : new Error(String(err));
      
      // If this is a timeout or abort error
      if (error.name === 'AbortError') {
        const timeoutError = enhanceError(
          new Error('Request timed out'),
          `API call to ${fn}`,
          'Request exceeded timeout limit'
        );
        timeoutError.code = 'TIMEOUT';
        timeoutError.type = ErrorType.TIMEOUT;
        timeoutError.isRetryable = true;
        lastError = timeoutError;
      } else {
        // Enhance the error with context
        lastError = enhanceError(
          error,
          `API call to ${fn}`,
          `Attempt ${attempt + 1} of ${retries + 1}`
        );
      }
      
      // If we have retries left and the error is retryable
      const isRetryable = (lastError as { isRetryable?: boolean }).isRetryable !== false;
      if (attempt < retries && isRetryable) {
        console.warn(
          `[API][req=${requestId}][${fn}] Retrying after error: ${lastError.message} (attempt ${attempt + 1} of ${retries + 1})`,
        );
        const cap = useMutationBackoff ? MUTATION_RETRY_CAP_MS : READ_RETRY_CAP_MS;
        const base = useMutationBackoff ? MUTATION_RETRY_BASE_MS : READ_RETRY_BASE_MS;
        const delayMs = exponentialBackoff
          ? Math.min(cap, base * Math.pow(2, attempt))
          : retryDelay;
        await new Promise(resolve => setTimeout(resolve, delayMs));
        attempt++;
        continue;
      }
      
      // No more retries or non-retryable error
      console.error(`[API][req=${requestId}][${fn}] Failed call after ${attempt + 1} attempts:`, lastError);
      // #region agent log
      if (fn === "bookings") {
        agentDebugLog({
          hypothesisId: "H1",
          location: "api.ts:call",
          message: "bookings_call_exhausted",
          data: {
            action: String(body.action ?? ""),
            attempts: attempt + 1,
            err: String(lastError?.message ?? "").slice(0, 200),
          },
        });
      }
      // #endregion
      throw lastError;
    }
  }
  
  // This should never be reached due to the throw in the catch block
  throw lastError || new Error('Unknown error occurred');
}

/** Use inside `cached()` fetchers only — safe for idempotent list/read actions. */
function callCachedRead<T>(fn: string, body: Record<string, unknown>, timeout = 30000): Promise<T> {
  return call<T>(fn, body, { exponentialBackoff: true, timeout });
}

// ---------------------------------------------------------------------------
// In-memory TTL cache — prevents redundant Firestore reads across re-renders,
// tab switches, and cascading useEffect chains.
// ---------------------------------------------------------------------------
const _cache = new Map<string, { data: unknown; expiry: number; inflight?: Promise<unknown> }>();

function cached<T>(key: string, ttlMs: number, fetcher: () => Promise<T>): Promise<T> {
  const hit = _cache.get(key);
  if (hit && Date.now() < hit.expiry) return Promise.resolve(hit.data as T);
  // Deduplicate concurrent in-flight requests for the same key
  if (hit?.inflight) return hit.inflight as Promise<T>;
  const p = fetcher().then(data => {
    _cache.set(key, { data, expiry: Date.now() + ttlMs });
    return data;
  }).catch(err => {
    _cache.delete(key);
    throw err;
  });
  _cache.set(key, { data: null, expiry: 0, inflight: p });
  return p;
}

// ---------------------------------------------------------------------------
// `listAdminWindow`: global 60s hard throttle (max one Netlify hit / minute unless forced).
// ---------------------------------------------------------------------------
const LIST_ADMIN_WINDOW_GLOBAL_THROTTLE_MS = 60_000;

type ListAdminWindowThrottled = { bookings: unknown[]; windowStart?: string };

let listAdminWindowLastFetchTime = 0;
let listAdminWindowCachedBookings: ListAdminWindowThrottled | null = null;
let listAdminWindowFetchInflight: Promise<ListAdminWindowThrottled> | null = null;
/** Bumped on cache bust so in-flight responses cannot repopulate after logout / `invalidateAdminDashboardCache`. */
let listAdminWindowThrottleEpoch = 0;

function resetListAdminWindowGlobalThrottle() {
  listAdminWindowLastFetchTime = 0;
  listAdminWindowCachedBookings = null;
  listAdminWindowThrottleEpoch++;
}

function invalidate(prefix: string) {
  for (const key of _cache.keys()) {
    if (key.startsWith(prefix)) _cache.delete(key);
  }
}

/** Force-clear the entire API cache (e.g. on logout). */
export function invalidateApiCache() {
  _cache.clear();
  resetListAdminWindowGlobalThrottle();
  listAdminWindowFetchInflight = null;
}

/** Clear cached booking lists for one user (e.g. so client sees new visits after refocus). */
export function invalidateBookingsForUser(email: string) {
  const e = (email || "").toLowerCase();
  _cache.delete(`bookings:user:${e}`);
  _cache.delete(`bookings:mine:${e}`);
  _cache.delete("bookings:assigned:me");
}

/** Bust dashboard read caches so LorenasPad Refresh always hits the server (clients + bookings stay fresh). */
export function invalidateAdminDashboardCache() {
  _cache.delete("bookings:listAdminWindow");
  resetListAdminWindowGlobalThrottle();
  _cache.delete("bookings:listBookingRequests");
  _cache.delete("users:listClients");
  _cache.delete("users:listTeam");
  _cache.delete("inquiries:list");
  _cache.delete("blocked:get");
}

/** Persist runtime secret and bust admin read caches so the next `listAdminWindow` sends the header. */
export function setRuntimeAdminSecretForLocalDev(secret: string | null | undefined) {
  if (typeof window === "undefined") return;
  try {
    const s = secret != null ? String(secret).trim() : "";
    if (s) localStorage.setItem(LORENA_RUNTIME_ADMIN_SECRET_KEY, s);
    else localStorage.removeItem(LORENA_RUNTIME_ADMIN_SECRET_KEY);
  } catch {
    /* ignore */
  }
  notifyRuntimeAdminSecretUpdated();
}

export function notifyRuntimeAdminSecretUpdated() {
  invalidateAdminDashboardCache();
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(LORENA_RUNTIME_ADMIN_SECRET_EVENT));
}

/** Shared implementation for admin client removal (Netlify `users` + Bearer auth). */
function deleteClientOnServer(email: string, uid?: string | null) {
  return call<{ ok: boolean }>("users", {
    action: "deleteClient",
    email: email || "",
    ...(uid ? { uid: String(uid).trim() } : {}),
  }).then((r) => {
    invalidate("users:");
    invalidate("bookings:");
    return r;
  });
}

// ---------------------------------------------------------------------------

const ONE_MIN = 60_000;
/** 5 minutes — used for heavy list endpoints to protect Firebase read quotas */
const FIVE_MIN = 300_000;
/** 30 seconds — used for user lookups and client-facing booking lists */
const THIRTY_SEC = 30_000;
const BOOKINGS_LIST_TTL = THIRTY_SEC;

export const api = {
  auth: {
    login: (email: string, password?: string) =>
      call<{ user: unknown; accessToken?: string }>("auth", {
        action: "login",
        email,
        ...(password ? { password } : {}),
      }),
    signup: (name: string, email: string, password?: string, phone?: string, address?: string, uid?: string) =>
      call<{ requiresVerification?: boolean; email?: string }>("auth", { action: "signup", name, email, password, phone, address, uid }),
    verifyEmail: (token: string) =>
      call<{ ok: boolean }>("auth", { action: "verifyEmail", token }),
  },

  users: {
    listAll: () =>
      cached("users:listAll", FIVE_MIN, async () => {
        const result = await callCachedRead<{ users?: unknown[] }>("users", { action: "listAll" });
        return { users: ensureArray(result?.users) };
      }),
    /** Admin: employees + admins only (smaller read than listAll). */
    listTeam: () =>
      cached("users:listTeam", FIVE_MIN, async () => {
        const result = await callCachedRead<{ users?: unknown[] }>("users", { action: "listTeam" });
        return { users: ensureArray(result?.users) };
      }),
    /** Admin: users with role client (legacy rows without role need listAll / Refresh users). */
    listClients: () =>
      cached("users:listClients", FIVE_MIN, async () => {
        const result = await callCachedRead<{ users?: unknown[] }>("users", { action: "listClients" });
        return { users: ensureArray(result?.users) };
      }),
    getByEmail: (email: string) =>
      cached(`users:get:${email.toLowerCase()}`, THIRTY_SEC, () => callCachedRead<{ user: unknown }>("users", { action: "getByEmail", email })),
    createEmployee: (name: string, email: string) =>
      call<{ ok: boolean }>("users", { action: "createEmployee", name, email })
        .then(r => { invalidate("users:"); return r; }),
    updatePlayerId: (email: string, playerId: string | null) =>
      call<{ ok: boolean }>("users", { action: "updatePlayerId", email, playerId }),
    updateUser: (email: string, updates: { name?: string; phone?: string; adminDisplayName?: string; address?: string; customRate?: number | null }) =>
      call<{ ok: boolean }>("users", { action: "updateUser", email, ...updates })
        .then(r => { invalidate("users:"); return r; }),
    changeEmail: (oldEmail: string, newEmail: string) =>
      call<{ ok: boolean }>("users", { action: "changeEmail", oldEmail, newEmail })
        .then(r => { invalidate("users:"); return r; }),
    deleteUser: (email: string) =>
      call<{ ok: boolean }>("users", { action: "deleteUser", email })
        .then(r => { invalidate("users:"); return r; }),
    /**
     * Admin: remove a client — deletes their bookings, legacy `users/{email}` doc (if any),
     * and artifacts `users/{uid}` tree when `uid` is provided (required for profile-missing rows).
     */
    deleteClient: (email: string, uid?: string | null) => deleteClientOnServer(email, uid),
    /** Hard-delete a user: sweeps all bookings, Firestore profile, and Firebase Auth entry. */
    deleteUserCompletely: (targetEmail: string, targetUid?: string | null) =>
      call<{ ok: boolean; deletedBookings?: number; message?: string }>("users", {
        action: "deleteUserCompletely",
        targetEmail: targetEmail || "",
        ...(targetUid ? { targetUid: String(targetUid).trim() } : {}),
      }).then((r) => { invalidate("users:"); invalidate("bookings:"); return r; }),
  },

  push: {
    send: (playerId: string, title: string, message: string) =>
      call<{ ok: boolean; skipped?: boolean }>("send-push", { playerId, title, message }),
  },

  bookings: {
    /**
     * Cached per normalized email. While the TTL entry is valid, the same `{ bookings }`
     * object reference is returned so consumers (e.g. effects) won’t see a “new” result every render.
     * Use `invalidateBookingsForUser(email)` before a forced refetch.
     */
    listByUser: (
      email: string,
      opts?: { limit?: number; cursor?: { rawDate: string; id: string } },
    ) => {
      const e = email.toLowerCase();
      const body: Record<string, unknown> = { action: "listByUser", email: e };
      if (opts?.limit != null) body.limit = opts.limit;
      if (opts?.cursor) body.cursor = opts.cursor;
      const normalize = (result: Record<string, unknown>) => ({
        bookings: ensureArray(result?.bookings),
        hasMore: !!result?.hasMore,
        nextCursor: (result?.nextCursor ?? null) as { rawDate: string; id: string } | null,
      });
      if (opts?.cursor) {
        return call<Record<string, unknown>>("bookings", body).then(normalize);
      }
      return cached(`bookings:user:${e}`, BOOKINGS_LIST_TTL, async () => {
        const result = await callCachedRead<Record<string, unknown>>("bookings", body);
        return normalize(result);
      });
    },
    listMine: (
      email: string,
      opts?: { limit?: number; cursor?: { rawDate: string; id: string } },
    ) => {
      const e = email.toLowerCase();
      const body: Record<string, unknown> = { action: "listMine", email: e };
      if (opts?.limit != null) body.limit = opts.limit;
      if (opts?.cursor) body.cursor = opts.cursor;
      const normalize = (result: Record<string, unknown>) => ({
        bookings: ensureArray(result?.bookings),
        hasMore: !!result?.hasMore,
        nextCursor: (result?.nextCursor ?? null) as { rawDate: string; id: string } | null,
      });
      if (opts?.cursor) {
        return call<Record<string, unknown>>("bookings", body).then(normalize);
      }
      return cached(`bookings:mine:${e}`, BOOKINGS_LIST_TTL, async () => {
        const result = await callCachedRead<Record<string, unknown>>("bookings", body);
        return normalize(result);
      });
    },
    /** Team: jobs assigned to the authenticated staff email (Bearer required). */
    listAssignedToMe: () =>
      cached("bookings:assigned:me", BOOKINGS_LIST_TTL, async () => {
        const result = await callCachedRead<{ bookings?: unknown[] }>("bookings", { action: "listAssignedToMe" });
        return { bookings: ensureArray(result?.bookings) };
      }),
    /**
     * Admin: rolling window + legacy active outside window (summary rows).
     * Hard-throttled to one real Netlify call per 60s unless `forceRefresh: true` (use after `invalidateAdminDashboardCache()` or for manual Refresh).
     */
    listAdminWindow: (opts?: { forceRefresh?: boolean }) => {
      const force = opts?.forceRefresh === true;
      const now = Date.now();
      if (
        !force &&
        listAdminWindowCachedBookings &&
        now - listAdminWindowLastFetchTime < LIST_ADMIN_WINDOW_GLOBAL_THROTTLE_MS
      ) {
        return Promise.resolve({
          bookings: [...listAdminWindowCachedBookings.bookings],
          windowStart: listAdminWindowCachedBookings.windowStart,
        });
      }
      if (listAdminWindowFetchInflight) {
        return listAdminWindowFetchInflight;
      }
      const epochAtStart = listAdminWindowThrottleEpoch;
      const flightHolder = {} as { p: Promise<ListAdminWindowThrottled> };
      flightHolder.p = callCachedRead<{ bookings?: unknown[]; windowStart?: string }>("bookings", {
        action: "listAdminWindow",
      })
        .then((result) => {
          if (epochAtStart !== listAdminWindowThrottleEpoch) {
            const snap = listAdminWindowCachedBookings;
            return {
              bookings: snap ? [...snap.bookings] : [],
              windowStart: snap?.windowStart,
            };
          }
          const data: ListAdminWindowThrottled = {
            bookings: ensureArray(result?.bookings),
            windowStart: typeof result?.windowStart === "string" ? result.windowStart : undefined,
          };
          listAdminWindowCachedBookings = data;
          listAdminWindowLastFetchTime = Date.now();
          return { bookings: [...data.bookings], windowStart: data.windowStart };
        })
        .finally(() => {
          if (listAdminWindowFetchInflight === flightHolder.p) {
            listAdminWindowFetchInflight = null;
          }
        });
      listAdminWindowFetchInflight = flightHolder.p;
      return flightHolder.p;
    },
    /** Full booking doc for edit / detail (1 read). */
    getById: (id: string) =>
      cached(`bookings:get:${id}`, ONE_MIN, async () => {
        const result = await callCachedRead<{ booking?: unknown }>("bookings", { action: "getById", id });
        return { booking: result?.booking ?? null };
      }),
    /** Paginated past visits (rawDate before today). Full docs per page. */
    listPastByUser: (
      email: string,
      opts?: {
        limit?: number;
        cursor?: { rawDate: string; id: string };
        /** YYYY-MM-DD upper bound for “past” (exclusive). Omit to use server “today” in `ianaTimeZone`. */
        asOfDate?: string;
        /** IANA zone for default “today” (matches cancel-future / portal calendar). */
        ianaTimeZone?: string;
      },
    ) =>
      call<{ bookings?: unknown[]; hasMore?: boolean; nextCursor?: { rawDate: string; id: string } | null }>("bookings", {
        action: "listPastByUser",
        email,
        ...(opts?.limit != null ? { limit: opts.limit } : {}),
        ...(opts?.cursor ? { cursor: opts.cursor } : {}),
        ...(opts?.asOfDate ? { asOfDate: opts.asOfDate } : {}),
        ...(opts?.ianaTimeZone ? { ianaTimeZone: opts.ianaTimeZone } : {}),
      }).then((r) => ({
        bookings: ensureArray(r?.bookings),
        hasMore: !!r?.hasMore,
        nextCursor: r?.nextCursor ?? null,
      })),
    listAll: () =>
      cached("bookings:listAll", FIVE_MIN, async () => {
        const result = await callCachedRead<{ bookings?: unknown[] }>("bookings", { action: "listAll" });
        return { bookings: ensureArray(result?.bookings) };
      }),
    /** Admin: documents in `booking_requests` (separate from `bookings` listAll). */
    listBookingRequests: () =>
      cached("bookings:listBookingRequests", THIRTY_SEC, async () => {
        const result = await callCachedRead<{ requests?: unknown[] }>("bookings", { action: "listBookingRequests" });
        return { requests: ensureArray(result?.requests) };
      }),
    add: (
      booking: unknown,
      targetEmail?: string,
      targetUid?: string | null,
      options?: { singleOccurrence?: boolean },
    ) => {
      const wireBooking = encodeBookingFieldsForWire(
        withViteFirebaseAppIdForWire({ ...(booking as Record<string, unknown>) } as EncodeInput),
      );
      const uid = targetUid != null && String(targetUid).trim() ? String(targetUid).trim() : undefined;
      const singleOccurrence = options?.singleOccurrence === true;
      assertPayloadUnderLimit(
        {
          booking: wireBooking,
          targetEmail,
          ...(uid ? { targetUid: uid } : {}),
          ...(singleOccurrence ? { singleOccurrence: true } : {}),
        },
        "Booking",
      );
      return call<{ bookings: unknown[] }>("bookings", {
        action: "add",
        booking: wireBooking,
        targetEmail,
        ...(uid ? { targetUid: uid } : {}),
        ...(singleOccurrence ? { singleOccurrence: true } : {}),
      }).then((r) => {
        invalidate("bookings:");
        _cache.delete("bookings:listAdminWindow");
        return r;
      });
    },
    update: (id: string, updates: Record<string, unknown>) =>
      call<{
        ok?: boolean;
        booking?: unknown;
        removedId?: string;
        bookings?: unknown[];
      }>("bookings", {
        action: "update",
        id,
        updates: encodeBookingFieldsForWire(withViteFirebaseAppIdForWire({ ...updates } as EncodeInput)),
      })
        .then((r) => {
          invalidate("bookings:");
          _cache.delete("bookings:listAdminWindow");
          _cache.delete(`bookings:get:${id}`);
          const rid = typeof r?.removedId === "string" ? r.removedId : null;
          if (rid) _cache.delete(`bookings:get:${rid}`);
          if (Array.isArray(r?.bookings)) {
            for (const row of r.bookings) {
              const bid = row && typeof row === "object" && "id" in row ? String((row as { id: unknown }).id) : "";
              if (bid) _cache.delete(`bookings:get:${bid}`);
            }
          } else if (r?.booking && typeof r.booking === "object" && "id" in r.booking) {
            _cache.delete(`bookings:get:${String((r.booking as { id: unknown }).id)}`);
          }
          return r;
        }),
    delete: (id: string) =>
      call<{ ok: boolean }>("bookings", { action: "delete", id }).then((r) => {
        invalidate("bookings:");
        _cache.delete("bookings:listAdminWindow");
        _cache.delete(`bookings:get:${id}`);
        return r;
      }),
    /** Chunked so `assertPayloadUnderLimit` never rejects on large "cancel all future" lists (each POST stays under 100KB). */
    deleteMany: (ids: string[]) => {
      const idSet = new Set<string>();
      for (const x of ids) {
        if (x === null || x === undefined) continue;
        const id = (typeof x === "string" ? x : String(x)).trim();
        if (id) idSet.add(id);
      }
      const uniqueIds = Array.from(idSet);
      const CHUNK = 280;

      const run = async (): Promise<{
        ok: boolean;
        deletedCount?: number;
        requestedCount?: number;
        missingIds?: string[];
      }> => {
        if (uniqueIds.length === 0) {
          return { ok: true, deletedCount: 0, requestedCount: 0 };
        }
        let deletedCount = 0;
        const missingAccum = new Set<string>();
        for (let i = 0; i < uniqueIds.length; i += CHUNK) {
          const slice = uniqueIds.slice(i, i + CHUNK);
          assertPayloadUnderLimit({ action: "deleteMany", ids: slice }, "deleteMany");
          const r = await call<{ ok?: boolean; deletedCount?: number; requestedCount?: number; missingIds?: string[] }>(
            "bookings",
            { action: "deleteMany", ids: slice },
          );
          deletedCount += typeof r.deletedCount === "number" ? r.deletedCount : slice.length;
          if (Array.isArray(r.missingIds)) {
            for (const m of r.missingIds) {
              if (typeof m === "string" && m) missingAccum.add(m);
            }
          }
        }
        invalidate("bookings:");
        _cache.delete("bookings:listAdminWindow");
        for (const id of uniqueIds) {
          _cache.delete(`bookings:get:${id}`);
        }
        return {
          ok: true,
          deletedCount,
          requestedCount: uniqueIds.length,
          ...(missingAccum.size > 0 ? { missingIds: Array.from(missingAccum).slice(0, 50) } : {}),
        };
      };

      return run();
    },
    /** Admin: query + soft-delete future visits for one client (server-side; avoids huge id payloads). */
    cancelFutureBookingsForClient: (
      fromRawDate: string,
      emailCandidates: string[],
      clientUid?: string | null,
      /** IANA zone from `Intl.DateTimeFormat().resolvedOptions().timeZone` so server matches portal calendar day for Timestamp `rawDate`. */
      clientTimeZone?: string | null,
    ) => {
      const d = (fromRawDate || "").trim();
      const emails = Array.from(
        new Set(
          emailCandidates
            .map((x) => String(x ?? "").trim())
            .filter((x) => x.length > 0),
        ),
      );
      const uid = typeof clientUid === "string" ? clientUid.trim() : "";
      const tz = typeof clientTimeZone === "string" ? clientTimeZone.trim() : "";
      if (!/^\d{4}-\d{2}-\d{2}$/.test(d) || (emails.length === 0 && !uid)) {
        return Promise.resolve({
          ok: true as const,
          deletedCount: 0,
          matchedIds: 0,
          capped: false,
        });
      }
      return call<{
        ok?: boolean;
        deletedCount?: number;
        matchedIds?: number;
        capped?: boolean;
        missingIds?: string[];
        error?: string;
        code?: string;
        _cancelFutureDebug?: Record<string, unknown>;
      }>("bookings", {
        action: "cancelFutureBookingsForClient",
        fromRawDate: d,
        emails,
        ...(uid ? { clientUid: uid } : {}),
        ...(tz ? { timeZone: tz } : {}),
      }, {
        extraHeaders:
          import.meta.env.DEV ? { "X-Lorena-Debug-Cancel-Future": "1" } : undefined,
      }).then((r) => {
        if (r && r.ok === false) {
          if (import.meta.env.DEV && r._cancelFutureDebug) {
            console.info("[LORENA_AGENT_DEBUG] cancelFuture response", r._cancelFutureDebug);
          }
          const msg =
            typeof r.error === "string" && r.error.trim()
              ? r.error.trim()
              : "Could not scan bookings to cancel future visits.";
          const err = new Error(msg) as Error & { code?: string };
          err.code = typeof r.code === "string" ? r.code : "CANCEL_FUTURE_SCAN_FAILED";
          throw err;
        }
        if (import.meta.env.DEV && r && r._cancelFutureDebug) {
          console.info("[LORENA_AGENT_DEBUG] cancelFuture response", r._cancelFutureDebug);
        }
        invalidate("bookings:");
        _cache.delete("bookings:listAdminWindow");
        for (const em of emails) {
          invalidateBookingsForUser(em);
        }
        return r;
      });
    },
    /** Admin: audit log for a booking (newest first). */
    listActivity: (bookingId: string, limit?: number) =>
      call<{ entries?: unknown[] }>("bookings", {
        action: "listActivity",
        bookingId,
        ...(limit != null ? { limit } : {}),
      }).then((r) => ({ entries: ensureArray(r?.entries) })),
    /** Admin: soft-deleted rows (`active === false`). */
    listArchived: () =>
      call<{ bookings?: unknown[] }>("bookings", { action: "listArchived" }).then((r) => ({
        bookings: ensureArray(r?.bookings),
      })),
    /** Admin: restore archived booking. */
    restore: (id: string) =>
      call<{ ok: boolean }>("bookings", { action: "restore", id }).then((r) => {
        invalidate("bookings:");
        _cache.delete("bookings:listAdminWindow");
        _cache.delete(`bookings:get:${id}`);
        return r;
      }),
  },

  requests: {
    /** Accept a booking_request: writes to bookings collection and deletes from booking_requests. */
    accept: (requestId: string, booking: unknown, targetEmail: string, options = {}) =>
      call<{ booking: unknown }>("bookings", { action: "acceptRequest", requestId, booking, targetEmail }, options)
        .then((r) => {
          invalidate("bookings:");
          _cache.delete("bookings:listAdminWindow");
          return r;
        }),
    /** Dismiss/decline a booking_request without scheduling. */
    dismiss: (requestId: string, options = {}) =>
      call<{ ok: boolean }>("bookings", { action: "deleteRequest", requestId }, options),
  },

  inquiries: {
    list: () =>
      cached("inquiries:list", ONE_MIN, async () => {
        const result = await callCachedRead<{ inquiries?: unknown[] }>("inquiries", { action: "list" });
        return { inquiries: ensureArray(result?.inquiries) };
      }),
    add: (name: string, email: string, message: string) =>
      call<{ inquiry: unknown }>("inquiries", { action: "add", name, email, message }).then((r) => {
        invalidate("inquiries:");
        return r;
      }),
    update: (id: string, updates: { status: "New" | "Replied" | "Converted" }) =>
      call<{ ok: boolean }>("inquiries", { action: "update", id, updates })
        .then(r => { invalidate("inquiries:"); return r; }),
  },

  blockedDays: {
    get: () =>
      cached("blocked:get", ONE_MIN, async () => {
        const result = await callCachedRead<{ blockedDays?: unknown }>("blocked-days", { action: "get" });
        return { blockedDays: ensureStringArray(result?.blockedDays) };
      }),
    toggle: (date: string) =>
      call<{ blockedDays: string[] }>("blocked-days", { action: "toggle", date })
        .then(r => { invalidate("blocked:"); return r; }),
  },

  payments: {
    record: (email: string, amount: number, bookingId?: string, method = "stripe") => {
      assertPayloadUnderLimit({ email, amount, bookingId, method }, "Payment");
      return call<{ payment: unknown }>("payments", { action: "record", email, amount, bookingId, method }).then((r) => {
        invalidate("payments:");
        invalidate("bookings:");
        _cache.delete("bookings:listAdminWindow");
        if (bookingId && typeof bookingId === "string") {
          _cache.delete(`bookings:get:${bookingId.trim()}`);
        }
        return r;
      });
    },
    listByUser: (email: string) =>
      cached(`payments:user:${email.toLowerCase()}`, THIRTY_SEC, async () => {
        const result = await callCachedRead<{ payments?: unknown[] }>("payments", { action: "listByUser", email });
        return { payments: ensureArray(result?.payments) };
      }),
    listAll: () =>
      cached("payments:listAll", THIRTY_SEC, async () => {
        const result = await callCachedRead<{ payments?: unknown[] }>("payments", { action: "listAll" });
        return { payments: ensureArray(result?.payments) };
      }),
  },

  sms: {
    sendReminder: (to: string, message: string) =>
      call<{ ok: boolean; skipped?: boolean }>("send-sms", { to, message }),
  },

  /** Same as `users.deleteClient` — admin client removal via Netlify `users` function. */
  clients: {
    delete: (email: string, uid?: string | null) => deleteClientOnServer(email, uid),
  },
};

/** Check if the database API is available AND Firebase is properly configured.
 *
 * Returns false when:
 *   – The blocked-days endpoint throws (network error, 500, etc.)
 *   – The endpoint responds 200 but includes `_warning` — our graceful fallback
 *     added in blocked-days.js that fires when Firebase credentials are absent.
 *
 * This prevents the app from entering "database mode" and making dozens of
 * follow-up API calls that would all 503 when Firebase isn't configured.
 */
export async function isDatabaseAvailable(): Promise<boolean> {
  try {
    const result = await api.blockedDays.get();
    // If blocked-days.js returned a _warning it means Firebase credentials are
    // missing or broken — treat this as "not available" so the app falls back
    // to localStorage mode instead of hammering the API with doomed requests.
    if (result && typeof result === "object" && "_warning" in result && (result as { _warning?: unknown })._warning) {
      const rid = makeApiRequestId();
      console.warn(
        `[API][req=${rid}][isDatabaseAvailable] Firebase not configured:`,
        (result as { _warning: string })._warning,
      );
      return false;
    }
    return true;
  } catch {
    return false;
  }
}
